// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TextField from "@conklin/MobiusOverrides/TextField/TextField";
import Tooltip, { TooltipPresentationProps } from "@conklin/MobiusOverrides/Tooltip";
import IconMinus from "@conklin/XDL/Icons/IconMinus";
import IconNotificationWarning from "@conklin/XDL/Icons/IconNotificationWarning";
import IconPlus from "@conklin/XDL/Icons/IconPlus";
import { MAX_QTY } from "@conklin/XDL/constants";
import { mergeWithCss, xDefaultRules, xMask } from "@conklin/XDL/Utilities";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect, useState } from "react";

interface OwnProps extends Omit<TextFieldProps, "css"> {
    onDecreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    onIncreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    showActionButton?: boolean;
    showNoPriceAlert?: boolean;
    extendedStyles?: QtyFieldStyles;
    allowZero?: boolean;
    editable?: boolean;
    showLabel?: boolean;
    min?: number | string;
    validationRules?: string;
    multipleSaleQty?: number;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>, options?: { force?: boolean }) => void;
}

type Props = OwnProps;

export interface QtyFieldStyles {
    container?: InjectableCss;
    editableQuantityTextField?: TextFieldPresentationProps;
    wrapper?: InjectableCss;
    qtyLabel?: TypographyProps;
    valueText?: TypographyProps;
    actionButton?: ClickablePresentationProps;
    questionIconStyles?: IconProps;
    print?: InjectableCss;
    qtyLabelNotEditable?: TypographyProps;
    qtyErrorMessage?: TypographyProps;
    noPriceIcon?: IconPresentationProps;
    noPriceTooltip?: TooltipPresentationProps;
}

export const qtyFieldStyles: QtyFieldStyles = {};

const enterKey = 13;

const QtyField = ({
    extendedStyles,
    onIncreaseChangeHandler,
    onDecreaseChangeHandler,
    disabled: disabledProps = false,
    value: currentValue,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    label: labelOverride,
    showActionButton = true,
    showNoPriceAlert = false,
    allowZero = false,
    editable = true,
    showLabel = true,
    min = 0,
    validationRules,
    multipleSaleQty,
    ...qtyFieldsProps
}: Props) => {
    const [styles] = useState(() => mergeWithCss(qtyFieldStyles, extendedStyles));
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);
    const [qtyError, setQtyError] = useState("");

    let minQty = 0;
    let maxQty = MAX_QTY;

    (validationRules || xDefaultRules["qty"]).split("|").forEach(item => {
        const [attr, val] = item.split(":");
        if (attr === "min") {
            minQty = Math.max(Number(min), +val);
        }
        if (attr === "max") {
            maxQty = +val;
        }
    }, {});

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            // XNG-Changes: pass the updated quantity. Blur with no event leaves Redux on the previous qty.
            onBlurTextField?.({
                target: { value: `${currentValue ?? ""}` },
                currentTarget: { value: `${currentValue ?? ""}` },
            } as any);
            setCallOnSubmitSafety(false);
        }
    }, [callOnSubmitSafety, currentValue]);

    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, force: boolean | string = false) => {
        event.target.value = xMask.qty(event.target.value);
        // useMask calls onChange with the masked string as the second argument. Only an explicit true submits.
        onChangeTextField?.(event, { force: force === true });
    };

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey && !onKeyDownTextField) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        // if (event?.currentTarget) {
        //     onChangeHandler(event);
        // }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }
        let nextValue;

        // do rounding in case we have minimumOrderedQuantity and MultipleSaleQty
        if (multipleSaleQty && multipleSaleQty > 1) {
            nextValue =
                currentValue && isNumeric(`${currentValue}`) && Number(currentValue) - multipleSaleQty >= minQty
                    ? Number(currentValue) - multipleSaleQty
                    : multipleSaleQty;
        } else {
            nextValue =
                currentValue && isNumeric(`${currentValue}`) && Number(currentValue) - 1 >= minQty
                    ? Number(currentValue) - 1
                    : undefined;
        }

        if (allowZero && nextValue === undefined) {
            return;
            // eslint-disable-next-line no-else-return
        } else if (!allowZero && !nextValue) {
            return;
        }

        event.currentTarget.value = nextValue;
        event.target.value = nextValue;

        if (onDecreaseChangeHandler) {
            event = onDecreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    const onIncreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        let nextValue;
        // do rounding in case we have minimumOrderedQuantity and MultipleSaleQty
        if (multipleSaleQty && multipleSaleQty > 1) {
            nextValue =
                currentValue && isNumeric(`${currentValue}`) && Number(currentValue) + multipleSaleQty <= maxQty
                    ? Number(currentValue) + multipleSaleQty
                    : undefined;
        } else {
            nextValue =
                currentValue && isNumeric(`${currentValue}`) && Number(currentValue) + 1 <= maxQty
                    ? Number(currentValue) + 1
                    : undefined;
        }

        if (!nextValue) {
            return;
        }

        event.currentTarget.value = nextValue;
        event.target.value = nextValue;

        if (onIncreaseChangeHandler) {
            event = onIncreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    const qtyTextField = (
        <TextField
            type="qty"
            disabled={disabled}
            value={currentValue}
            onChange={onChangeHandler}
            onBlur={onBlurHandler}
            onClick={onClickHandler}
            onKeyDown={onKeyDownHandler}
            {...styles.editableQuantityTextField}
            {...qtyFieldsProps}
            aria-label={translate("QTY")}
            validationRules={validationRules}
            // XNG-Changes
            onError={(message: any) => setQtyError(message)}
            showError={false}
            {...{ showActionButton }}
        />
    );
    if (editable) {
        return (
            <StyledWrapper {...styles.container} data-test-selector="qty-field">
                {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                <VisuallyHidden as="label">{translate("QTY")}</VisuallyHidden>
                <StyledWrapper {...styles.wrapper} {...{ showActionButton }}>
                    {showActionButton && (
                        <Clickable
                            {...styles.actionButton}
                            disabled={disabled || Number(currentValue) <= minQty}
                            onClick={onDecreaseClickHandler}
                            data-test-selector="product_qtyOrdered_decrease"
                            aria-label="decrease quantity"
                        >
                            <Icon src={IconMinus} size={10} />
                        </Clickable>
                    )}
                    {qtyTextField}
                    {showActionButton && (
                        <Clickable
                            {...styles.actionButton}
                            disabled={disabled || Number(currentValue) >= maxQty}
                            onClick={onIncreaseClickHandler}
                            data-test-selector="product_qtyOrdered_increase"
                            aria-label="increase quantity"
                        >
                            <Icon src={IconPlus} size={10} />
                        </Clickable>
                    )}
                </StyledWrapper>
                {showNoPriceAlert && (
                    <Tooltip
                        text={siteMessage("Custom_CartLine_NoPrice_or_QuoteRequired")}
                        triggerComponent={<Icon {...styles.noPriceIcon} src={IconNotificationWarning} />}
                        {...styles.noPriceTooltip}
                    />
                )}
                {qtyError && <Typography {...styles.qtyErrorMessage}>{qtyError}</Typography>}
            </StyledWrapper>
        );
    }
    return (
        <>
            <VisuallyHidden as="label">{labelOverride || translate("QTY")}</VisuallyHidden>
            {showLabel && <Typography {...styles.qtyLabelNotEditable}>{labelOverride || translate("QTY")}</Typography>}
            <Typography {...styles.valueText}>{currentValue}</Typography>
        </>
    );
};

export default QtyField;
